/**
 * Base class for fade-based slideshows with GSAP animations
 */
class FadeSlideshow {
  constructor(config) {
    this.container = config.container;
    this.slides = config.slides;
    this.currentSlide = 0;
    this.totalSlides = this.slides.length;
    this.isAnimating = false;
    this.useGsap = typeof gsap !== 'undefined';
    
    this.config = {
      autoplay: config.autoplay || false,
      autoplaySpeed: config.autoplaySpeed || 5000,
      mediaSelector: config.mediaSelector || null,
      contentSelector: config.contentSelector || null,
      onSlideChange: config.onSlideChange || null,
      ...config
    };
    
    this.autoplayTimer = null;
    this.isPlaying = this.config.autoplay;
    this.isPausedByUser = false;
    
    if (!this.useGsap) {
      console.warn('GSAP not loaded, falling back to CSS transitions');
    }
    
    this.initSlides();
  }

  initSlides() {
    this.slides.forEach((slide, index) => {
      if (index !== 0) {
        slide.style.opacity = '0';
        slide.style.visibility = 'hidden';
        slide.setAttribute('aria-hidden', 'true');
        this.pauseSlideVideos(slide);
      } else {
        slide.style.opacity = '1';
        slide.style.visibility = 'visible';
        slide.removeAttribute('aria-hidden');
        this.playSlideVideos(slide);
        slide.classList.add('is-active');
      }
    });
  }

  goTo(index, animate = true) {
    if (this.isAnimating || this.totalSlides === 0) return;
    
    const previousSlide = this.currentSlide;
    
    if (index < 0) {
      this.currentSlide = this.totalSlides - 1;
    } else if (index >= this.totalSlides) {
      this.currentSlide = 0;
    } else {
      this.currentSlide = index;
    }
    
    if (previousSlide === this.currentSlide) return;
    
    const currentSlideElement = this.slides[this.currentSlide];
    const previousSlideElement = this.slides[previousSlide];
    
    if (this.useGsap && animate) {
      this.animateTransition(previousSlideElement, currentSlideElement);
    } else {
      this.fallbackTransition(previousSlideElement, currentSlideElement);
    }
    
    if (this.config.onSlideChange) {
      this.config.onSlideChange(this.currentSlide);
    }
  }

  animateTransition(previousSlide, currentSlide) {
    this.isAnimating = true;
    
    const previousMedia = previousSlide.querySelectorAll(this.config.mediaSelector || '*');
    const currentMedia = currentSlide.querySelectorAll(this.config.mediaSelector || '*');
    const contentElements = currentSlide.querySelectorAll(this.config.contentSelector || '*');
    const previousContent = previousSlide.querySelectorAll(this.config.contentSelector || '*');
    
    this.pauseSlideVideos(previousSlide);
    
    currentSlide.style.visibility = 'visible';
    currentSlide.classList.add('is-active');
    currentSlide.removeAttribute('aria-hidden');
    this.playSlideVideos(currentSlide);
    
    const tl = gsap.timeline({
      onComplete: () => {
        previousSlide.style.visibility = 'hidden';
        previousSlide.classList.remove('is-active');
        previousSlide.setAttribute('aria-hidden', 'true');
        this.isAnimating = false;
        
        gsap.set(previousMedia, { clearProps: 'all' });
        gsap.set(previousContent, { clearProps: 'all' });
      }
    });
    
    if (previousContent.length > 0) {
      tl.to(previousContent, {
        opacity: 0,
        y: -20,
        duration: 0.5,
        stagger: 0.08,
        ease: 'power2.in'
      }, 0);
    }
    
    if (previousMedia.length > 0) {
      tl.to(previousMedia, {
        scale: 1.05,
        duration: 0.8,
        ease: 'power1.inOut'
      }, 0);
    }
    
    tl.to(previousSlide, {
      opacity: 0,
      duration: 0.8,
      ease: 'power2.inOut'
    }, 0.3);
    
    if (currentMedia.length > 0) {
      tl.fromTo(currentMedia, 
        {
          opacity: 0,
          scale: 1.15,
          transformOrigin: 'center center'
        },
        {
          opacity: 1,
          scale: 1,
          duration: 1.6,
          ease: 'power2.out'
        }, 
        0.5
      );
    }
    
    tl.to(currentSlide, {
      opacity: 1,
      duration: 1,
      ease: 'power2.inOut'
    }, 0.4);
    
    if (contentElements.length > 0) {
      tl.fromTo(contentElements,
        {
          opacity: 0,
          y: 40,
          scale: 0.98
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 1,
          stagger: {
            amount: 0.25,
            ease: 'power2.out'
          },
          ease: 'power3.out'
        },
        0.9
      );
    }
  }

  fallbackTransition(previousSlide, currentSlide) {
    this.pauseSlideVideos(previousSlide);
    
    previousSlide.classList.remove('is-active');
    previousSlide.setAttribute('aria-hidden', 'true');
    previousSlide.style.opacity = '0';
    previousSlide.style.visibility = 'hidden';
    
    currentSlide.classList.add('is-active');
    currentSlide.removeAttribute('aria-hidden');
    currentSlide.style.opacity = '1';
    currentSlide.style.visibility = 'visible';
    
    this.playSlideVideos(currentSlide);
  }

  next() {
    this.goTo(this.currentSlide + 1);
  }

  previous() {
    this.goTo(this.currentSlide - 1);
  }

  startAutoplay() {
    if (this.totalSlides <= 1) return;
    
    this.autoplayTimer = setInterval(() => {
      this.next();
    }, this.config.autoplaySpeed);
    
    this.isPlaying = true;
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
    this.isPlaying = false;
  }

  pauseAutoplay() {
    if (this.config.autoplay && this.isPlaying && !this.isPausedByUser) {
      this.stopAutoplay();
    }
  }

  resumeAutoplay() {
    if (this.config.autoplay && !this.isPlaying && !this.isPausedByUser) {
      this.startAutoplay();
    }
  }

  resetAutoplay() {
    if (this.config.autoplay && this.isPlaying && !this.isPausedByUser) {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }

  toggleAutoplay() {
    if (this.isPlaying) {
      this.stopAutoplay();
      this.isPausedByUser = true;
    } else {
      this.startAutoplay();
      this.isPausedByUser = false;
    }
    return this.isPlaying;
  }

  playSlideVideos(slide) {
    const videos = slide.querySelectorAll('video');
    videos.forEach(video => {
      video.currentTime = 0;
      video.play().catch(() => {});
    });
  }

  pauseSlideVideos(slide) {
    const videos = slide.querySelectorAll('video');
    videos.forEach(video => {
      video.pause();
    });
  }

  destroy() {
    this.stopAutoplay();
  }
}

/**
 * Hero Banner Web Component
 */
class HeroBannerComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('.hero-banner__slider');
    this.slides = this.querySelectorAll('.hero-banner__slide');
    this.prevButtons = this.querySelectorAll('.hero-banner__button--prev');
    this.nextButtons = this.querySelectorAll('.hero-banner__button--next');
    this.dots = this.querySelectorAll('.hero-banner__dot');
    this.autoplayButton = this.querySelector('.hero-banner__autoplay');
    this.counterCurrent = this.querySelectorAll('.hero-banner__counter-current');
    
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchEndX = 0;
    this.touchEndY = 0;
    this.isDragging = false;
    this.swipeDirection = null;
    
    this.init();
  }

  init() {
    if (this.slides.length === 0) return;
    
    const autoplay = this.slider.dataset.autoplay === 'true';
    const autoplaySpeed = parseInt(this.slider.dataset.speed) * 1000 || 5000;
    
    this.slideshow = new FadeSlideshow({
      container: this.slider,
      slides: this.slides,
      autoplay: autoplay,
      autoplaySpeed: autoplaySpeed,
      mediaSelector: '.banner-desktop__media, .banner-mobile__media',
      contentSelector: '.banner__heading, .banner__subheading, .banner__richtext, .banner__cta',
      onSlideChange: (index) => this.updateNavigation(index)
    });
    
    this.bindEvents();
    this.observeSlides();
    
    if (autoplay) {
      this.slideshow.startAutoplay();
    }
    
    this.updateNavigation(0);
    this.updateAutoplayButton(autoplay);
  }

  bindEvents() {
    this.prevButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.slideshow.previous();
        this.slideshow.resetAutoplay();
      });
    });
    
    this.nextButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.slideshow.next();
        this.slideshow.resetAutoplay();
      });
    });
    
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.slideshow.goTo(index);
        this.slideshow.resetAutoplay();
      });
    });
    
    if (this.autoplayButton) {
      this.autoplayButton.addEventListener('click', () => {
        const isPlaying = this.slideshow.toggleAutoplay();
        this.updateAutoplayButton(isPlaying);
      });
    }
    
    this.slider.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: true });
    this.slider.addEventListener('touchmove', (e) => this.handleTouchMove(e), { passive: false });
    this.slider.addEventListener('touchend', (e) => this.handleTouchEnd(e), { passive: true });
    
    this.slider.addEventListener('mouseenter', () => this.slideshow.pauseAutoplay());
    this.slider.addEventListener('mouseleave', () => this.slideshow.resumeAutoplay());
    
    document.addEventListener('keydown', (e) => this.handleKeyDown(e));
  }

  updateNavigation(index) {
    this.dots.forEach((dot, i) => {
      if (i === index) {
        dot.classList.add('is-active');
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.classList.remove('is-active');
        dot.removeAttribute('aria-current');
      }
    });
    
    this.counterCurrent.forEach(counter => {
      counter.textContent = index + 1;
    });
    
    this.slider.setAttribute('aria-live', 'polite');
  }

  updateAutoplayButton(isPlaying) {
    if (!this.autoplayButton) return;
    
    const pauseIcon = this.autoplayButton.querySelector('.hero-banner__autoplay-icon--pause');
    const playIcon = this.autoplayButton.querySelector('.hero-banner__autoplay-icon--play');
    
    if (isPlaying) {
      this.autoplayButton.classList.remove('is-paused');
      this.autoplayButton.setAttribute('aria-label', 'Pause slideshow');
      if (pauseIcon) pauseIcon.style.display = '';
      if (playIcon) playIcon.style.display = 'none';
    } else {
      this.autoplayButton.classList.add('is-paused');
      this.autoplayButton.setAttribute('aria-label', 'Play slideshow');
      if (pauseIcon) pauseIcon.style.display = 'none';
      if (playIcon) playIcon.style.display = '';
    }
  }

  handleTouchStart(e) {
    this.touchStartX = e.changedTouches[0].clientX;
    this.touchStartY = e.changedTouches[0].clientY;
    this.isDragging = true;
    this.swipeDirection = null;
  }

  handleTouchMove(e) {
    if (!this.isDragging) return;
    
    const currentX = e.changedTouches[0].clientX;
    const currentY = e.changedTouches[0].clientY;
    const diffX = Math.abs(currentX - this.touchStartX);
    const diffY = Math.abs(currentY - this.touchStartY);
    
    if (this.swipeDirection === null && (diffX > 10 || diffY > 10)) {
      this.swipeDirection = diffX > diffY ? 'horizontal' : 'vertical';
    }
    
    if (this.swipeDirection === 'horizontal') {
      e.preventDefault();
    }
  }

  handleTouchEnd(e) {
    if (!this.isDragging) return;
    
    this.touchEndX = e.changedTouches[0].clientX;
    this.touchEndY = e.changedTouches[0].clientY;
    this.handleSwipe();
    
    this.isDragging = false;
    this.swipeDirection = null;
  }

  handleSwipe() {
    const swipeThreshold = 50;
    const diffX = this.touchStartX - this.touchEndX;
    const diffY = Math.abs(this.touchStartY - this.touchEndY);
    
    if (this.swipeDirection === 'horizontal' && Math.abs(diffX) > swipeThreshold && diffY < Math.abs(diffX)) {
      if (diffX > 0) {
        this.slideshow.next();
        this.slideshow.resetAutoplay();
      } else {
        this.slideshow.previous();
        this.slideshow.resetAutoplay();
      }
    }
  }

  handleKeyDown(e) {
    if (!this.slider.matches(':hover')) return;
    
    switch(e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        this.slideshow.previous();
        this.slideshow.resetAutoplay();
        break;
      case 'ArrowRight':
        e.preventDefault();
        this.slideshow.next();
        this.slideshow.resetAutoplay();
        break;
    }
  }

  observeSlides() {
    if ('IntersectionObserver' in window) {
      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 0.5
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            if (entry.target.classList.contains('is-active') || entry.target.style.opacity === '1') {
              const videos = entry.target.querySelectorAll('video');
              videos.forEach(video => {
                video.play().catch(() => {});
              });
            }
          } else {
            const videos = entry.target.querySelectorAll('video');
            videos.forEach(video => {
              video.pause();
            });
          }
        });
      }, options);

      this.slides.forEach(slide => {
        observer.observe(slide);
      });
    }
  }

  disconnectedCallback() {
    if (this.slideshow) {
      this.slideshow.destroy();
    }
  }
}

if (!customElements.get('hero-banner-component')) {
  customElements.define('hero-banner-component', HeroBannerComponent);
}

